import { normalizeListPage } from '@/utils/listPage';
import { history, request } from '@umijs/max';
import type { TaxRateBusinessType } from '@/constants/enums';

export type BusinessReferenceResource =
  | 'persons'
  | 'departments'
  | 'organizations'
  | 'customers'
  | 'customer-categories'
  | 'customer-contacts'
  | 'suppliers'
  | 'supplier-contacts'
  | 'vessels'
  | 'equipment'
  | 'equipment-models'
  | 'manufacturers'
  | 'materials'
  | 'material-categories'
  | 'spare-parts'
  | 'measurement-units'
  | 'product-qualities'
  | 'warehouses'
  | 'currencies'
  | 'tax-rates'
  | 'payment-terms'
  | 'shipping-terms'
  | 'banks'
  | 'hs-codes'
  | 'code-archives'
  | 'product-names'
  | 'roles';

export interface BusinessReferencePageRequest {
  current?: number;
  pageSize?: number;
  keyword?: string;
  status?: string;
  businessType?: TaxRateBusinessType;
  deptId?: number;
  customerId?: number;
  supplierId?: number;
  manufacturerId?: number;
  equipmentId?: number;
  equipmentModelId?: number;
  sparePartId?: number;
  materialType?: string;
  supplierType?: string;
  country?: string;
  basicCategory?: string;
  category?: string;
  sparePartCode?: string;
  requireAccount?: boolean;
}

export interface PersonReferenceRequest {
  current?: number;
  pageSize?: number;
  keyword?: string;
  deptId?: number;
}

export interface PersonReferenceOption {
  id: number;
  code: string;
  name: string;
  username: string;
  usernameEn?: string;
  email?: string;
  deptId?: number;
  deptName?: string;
}

export interface DepartmentReferenceOption {
  id: number;
  code: string;
  name: string;
  parentId?: number;
  children?: DepartmentReferenceOption[];
}

export interface RoleReferenceOption {
  id: number;
  code: string;
  name: string;
}

export interface ReferencePage<T> {
  data: T[];
  total: number;
  current: number;
  pageSize: number;
  success?: boolean;
}

export interface ReferenceResult<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface CurrentArchiveIdentityTarget {
  type: string;
  id: number;
  parentId?: number;
}

export interface CurrentArchiveIdentity extends CurrentArchiveIdentityTarget {
  code?: string;
  name?: string;
  imoNumber?: string;
  partNumber?: string;
}

export interface NormalizedReferencePage<T> {
  success: boolean;
  data: T[];
  total: number;
  current: number;
  pageSize: number;
}

export const MAX_REFERENCE_PAGE_SIZE = 100;
export const MAX_REFERENCE_PAGES = 200;
export const MAX_REFERENCE_ROWS =
  MAX_REFERENCE_PAGE_SIZE * MAX_REFERENCE_PAGES;

/**
 * 兼容 Umi 响应拦截器的扁平分页壳，以及未经过拦截器时的 Result<PageResult<T>>。
 */
export function normalizeReferencePage<T>(
  response: unknown,
): NormalizedReferencePage<T> {
  const page = normalizeListPage<T>(response);
  return {
    ...page,
    current: page.current ?? 1,
    pageSize: page.pageSize ?? page.data.length,
  };
}

/** 获取任意已登记档案的只读分页参照。 */
export async function getBusinessReferencePage<T = any>(
  resource: BusinessReferenceResource,
  params: BusinessReferencePageRequest = {},
) {
  return request<ReferenceResult<ReferencePage<T>>>(
    `/api/references/${resource}`,
    {
      method: 'GET',
      params,
    },
  );
}

/**
 * 仅凭稳定档案主键批量读取当前身份字段。业务单据中的名称、编码快照不参与反查。
 */
export async function getCurrentArchiveIdentities(
  targets: CurrentArchiveIdentityTarget[],
): Promise<CurrentArchiveIdentity[]> {
  const response = await request<
    ReferenceResult<CurrentArchiveIdentity[]> | CurrentArchiveIdentity[]
  >('/api/references/current-identities', {
    method: 'POST',
    // This is a read-only batch lookup; the global GET deadline does not apply.
    timeout: 45_000,
    data: { targets },
  });
  const raw = response as any;
  const payload =
    raw?.data &&
    !Array.isArray(raw.data) &&
    typeof raw.data === 'object' &&
    ('success' in raw.data || Array.isArray(raw.data?.data))
      ? raw.data
      : raw;
  if (payload?.success === false) {
    throw new Error(payload.message || '档案反查失败');
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  throw new Error('档案反查响应格式错误');
}

/**
 * 同一次「页面停留」内复用全表结果，路由变化时整体清空。
 *
 * 生命周期绑路由而不是绑 TTL：TTL 会让新建的客户/仓库在该窗口内不出现在下拉里。
 * 若将来加了「页内直接新建档案」的入口，必须在写入成功后调用 clearReferenceCache()。
 */
const referenceCache = new Map<string, NormalizedReferencePage<any>>();

/**
 * 缓存代次：清空后自增。用于丢弃「清空之前发出、清空之后才返回」的响应，
 * 避免它把陈旧结果写回新页面的缓存。
 */
let referenceCacheGeneration = 0;

/** 缓存所属的路由键（pathname + search）。路由一变即整体作废。 */
let referenceCacheRoute: string | undefined;
let referenceCacheListening = false;

function currentRouteKey(): string | undefined {
  try {
    return `${history.location.pathname}${history.location.search}`;
  } catch {
    return undefined;
  }
}

/**
 * 首次读取时注册路由监听，离页即失效。仅在读取时比较 URL 会漏掉
 * 「业务页 → 不读取参照的档案页 → 原业务页」这类往返。
 * 无浏览器路由监听的环境不保留缓存，避免无法判断页面生命周期。
 */
function ensureCacheRoute(): void {
  if (!referenceCacheListening) {
    try {
      history.listen(() => clearReferenceCache());
      referenceCacheListening = true;
    } catch {
      // 非浏览器环境允许读取，但不复用跨次结果。
    }
  }
  const route = currentRouteKey();
  if (!referenceCacheListening || route === undefined || route !== referenceCacheRoute) {
    clearReferenceCache();
  }
}

/** 清空参照全表缓存。路由变化会自动触发；档案写入路径也可显式调用。 */
export function clearReferenceCache(): void {
  referenceCacheGeneration += 1;
  referenceCache.clear();
  inflightReferenceLoads.clear();
  referenceCacheRoute = currentRouteKey();
}

/**
 * 同一时刻、同一 (resource, params) 的在途请求合并成一次，调用方共享同一个 Promise。
 *
 * 典型收益：同一页面上多个参照组件（或详情页多行）同时展开同一档案时，
 * 原本会各自触发一遍「逐页串行拉全表」，现在只跑一遍。
 */
const inflightReferenceLoads = new Map<
  string,
  Promise<NormalizedReferencePage<any>>
>();

function referenceLoadKey(
  resource: BusinessReferenceResource,
  params: unknown,
): string {
  // params 是浅层筛选对象；键序不同只会导致少合并一次，不影响正确性。
  return `${resource}|${JSON.stringify(params ?? {})}`;
}

export async function getAllBusinessReferenceRows<T = any>(
  resource: BusinessReferenceResource,
  params: Omit<BusinessReferencePageRequest, 'current' | 'pageSize'> = {},
): Promise<NormalizedReferencePage<T>> {
  // 路由已变则先作废上一次页面停留期间缓存的全表结果
  ensureCacheRoute();

  const key = referenceLoadKey(resource, params);

  const cached = referenceCache.get(key);
  if (cached) {
    // 命中缓存时给出浅拷贝的壳与数组：调用方对数组做的排序/增删不会影响其他调用方。
    // 行对象本身仍是同一批引用，与「同一页内共享一份全表」的语义一致，请勿就地修改行数据。
    return {
      ...cached,
      data: cached.data.slice(),
    } as NormalizedReferencePage<T>;
  }

  const existing = inflightReferenceLoads.get(key);
  if (existing) {
    return existing as Promise<NormalizedReferencePage<T>>;
  }

  const generation = referenceCacheGeneration;
  const pending = getAllBusinessReferenceRowsUncached<T>(resource, params);
  inflightReferenceLoads.set(
    key,
    pending as Promise<NormalizedReferencePage<any>>,
  );
  // 用 then 的两个回调而不是 finally，以免额外派生出一条可能无人接管的 rejection 链。
  void pending.then(
    (value) => {
      if (inflightReferenceLoads.get(key) === pending) {
        inflightReferenceLoads.delete(key);
      }
      // 只在成功时写入缓存（失败不缓存，下次自然重试）；
      // 且仅当期间没发生过清空才写回，否则会污染新页面的缓存。
      if (generation === referenceCacheGeneration) {
        referenceCache.set(key, value as NormalizedReferencePage<any>);
      }
    },
    () => {
      if (inflightReferenceLoads.get(key) === pending) {
        inflightReferenceLoads.delete(key);
      }
    },
  );
  return pending;
}

/**
 * 为需要客户端全量映射/过滤的旧消费者逐页汇总。
 * 新参照接口单页上限为 100；页数、总行数和重复页均有保护，避免静默截断或死循环。
 */
async function getAllBusinessReferenceRowsUncached<T = any>(
  resource: BusinessReferenceResource,
  params: Omit<BusinessReferencePageRequest, 'current' | 'pageSize'> = {},
): Promise<NormalizedReferencePage<T>> {
  const rows: T[] = [];
  const seenPages = new Set<string>();
  let total = 0;

  for (let current = 1; current <= MAX_REFERENCE_PAGES; current += 1) {
    const response = await getBusinessReferencePage<T>(resource, {
      ...params,
      current,
      pageSize: MAX_REFERENCE_PAGE_SIZE,
    });
    const page = normalizeReferencePage<T>(response);
    const pageRows = page.data;

    if (!page.success) {
      throw new Error(`加载 ${resource} 参照失败`);
    }

    if (pageRows.length === 0) break;

    const pageSignature = pageRows
      .map((row: any) => String(row?.id ?? row?.code ?? ''))
      .join('|');
    if (seenPages.has(pageSignature)) break;
    seenPages.add(pageSignature);

    rows.push(
      ...pageRows.slice(
        0,
        Math.max(0, MAX_REFERENCE_ROWS - rows.length),
      ),
    );
    total = Math.max(total, page.total);

    if (
      rows.length >= MAX_REFERENCE_ROWS ||
      rows.length >= page.total ||
      pageRows.length < MAX_REFERENCE_PAGE_SIZE
    ) {
      break;
    }
  }

  return {
    success: true,
    data: rows,
    total: total || rows.length,
    current: 1,
    pageSize: rows.length,
  };
}

/**
 * 获取业务单据可用的人员参照数据。
 *
 * 该接口只返回可选择的最小人员信息，不复用系统用户管理列表。
 */
export async function getPersonReferencePage(params: PersonReferenceRequest) {
  const { current = 1, pageSize = 10, keyword, deptId } = params;
  return request<ReferenceResult<ReferencePage<PersonReferenceOption>>>(
    '/api/references/persons',
    {
      method: 'GET',
      params: {
        current,
        pageSize,
        keyword,
        deptId,
      },
    },
  );
}

/**
 * 获取业务单据可用的部门参照树。
 *
 * 返回值仍是树形数据，供现有部门树界面直接使用。
 */
export async function getDepartmentReferenceTree(params?: {
  keyword?: string;
}) {
  return request<ReferenceResult<DepartmentReferenceOption[]>>(
    '/api/references/departments',
    {
      method: 'GET',
      params,
    },
  );
}

/**
 * 批量获取流程中已保存的人员，用于历史 FIXED_USER 配置回显。
 * 后端允许返回当前已停用人员；每次最多查询 100 个去重 ID。
 */
export async function getPersonReferencesByIds(
  ids: Array<number | string>,
): Promise<ReferenceResult<PersonReferenceOption[]>> {
  const normalizedIds = Array.from(
    new Set(
      ids
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0),
    ),
  ).slice(0, 100);

  if (normalizedIds.length === 0) {
    return { success: true, data: [] };
  }

  return request<ReferenceResult<PersonReferenceOption[]>>(
    '/api/references/persons/by-ids',
    {
      method: 'GET',
      params: { ids: normalizedIds.join(',') },
    },
  );
}

export async function getRoleReferences() {
  return request<ReferenceResult<RoleReferenceOption[]>>(
    '/api/references/roles',
    { method: 'GET' },
  );
}

export async function getOrganizationReferenceTree(params?: {
  keyword?: string;
}) {
  return request<ReferenceResult<any[]>>('/api/references/organizations', {
    method: 'GET',
    params,
  });
}

export async function getCustomerCategoryReferences(params?: {
  keyword?: string;
}) {
  return request<ReferenceResult<string[]>>(
    '/api/references/customer-categories',
    {
      method: 'GET',
      params,
    },
  );
}

export async function getMaterialCategoryReferences(params?: {
  keyword?: string;
}) {
  return request<ReferenceResult<Array<{
    value: string;
    label: string;
    count: number;
  }>>>(
    '/api/references/material-categories',
    {
      method: 'GET',
      params,
    },
  );
}

export const getCustomerReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('customers', params);

export const getCustomerContactReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('customer-contacts', params);

export const getSupplierReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('suppliers', params);

export const getSupplierContactReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('supplier-contacts', params);

export const getVesselReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('vessels', params);

export const getEquipmentReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('equipment', params);

export const getEquipmentModelReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('equipment-models', params);

export const getManufacturerReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('manufacturers', params);

export const getMaterialReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('materials', params);

export const getSparePartReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('spare-parts', params);

export const getMeasurementUnitReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('measurement-units', params);

export const getWarehouseReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('warehouses', params);

export const getCurrencyReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('currencies', params);

export async function getCurrencyReferenceDetail<T = any>(
  id?: number | null,
): Promise<T | null> {
  const archiveId = Number(id);
  if (!Number.isInteger(archiveId) || archiveId <= 0) return null;
  try {
    const response: any = await request<ReferenceResult<T>>(
      `/api/references/currencies/${archiveId}`,
      { method: 'GET' },
    );
    return response?.data ?? null;
  } catch {
    return null;
  }
}

export const getTaxRateReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('tax-rates', params);

export const getPaymentTermReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('payment-terms', params);

export const getShippingTermReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('shipping-terms', params);

export const getBankReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('banks', params);

export const getHsCodeReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('hs-codes', params);

export const getCodeArchiveReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('code-archives', params);

export const getProductNameReferencePage = <T = any>(
  params: BusinessReferencePageRequest = {},
) => getBusinessReferencePage<T>('product-names', params);

export async function getCustomerReferenceDetail<T = any>(id: number) {
  return request<ReferenceResult<T>>(`/api/references/customers/${id}`, {
    method: 'GET',
  });
}

export async function getDefaultCustomerContactReference<T = any>(
  customerId: number,
): Promise<T | undefined> {
  try {
    const response: any = await request<ReferenceResult<T>>(
      `/api/references/customers/${customerId}/default-contact`,
      { method: 'GET' },
    );
    return response?.data ?? undefined;
  } catch {
    return undefined;
  }
}

export async function getSupplierReferenceDetail<T = any>(id: number) {
  return request<ReferenceResult<T>>(`/api/references/suppliers/${id}`, {
    method: 'GET',
  });
}

export async function getPaymentTermReferenceDetail<T = any>(id: number) {
  return request<ReferenceResult<T>>(`/api/references/payment-terms/${id}`, {
    method: 'GET',
  });
}

export const getAllPersonReferences = (params: {
  keyword?: string;
  deptId?: number;
} = {}) => getAllBusinessReferenceRows<PersonReferenceOption>('persons', params);

export const getAllCustomerReferences = <T = any>(
  params: Omit<BusinessReferencePageRequest, 'current' | 'pageSize'> = {},
) => getAllBusinessReferenceRows<T>('customers', params);

export const getAllCustomerContactReferences = <T = any>(
  params: Omit<BusinessReferencePageRequest, 'current' | 'pageSize'> = {},
) => getAllBusinessReferenceRows<T>('customer-contacts', params);

export const getAllSupplierReferences = <T = any>(
  params: Omit<BusinessReferencePageRequest, 'current' | 'pageSize'> = {},
) => getAllBusinessReferenceRows<T>('suppliers', params);

export const getAllSupplierContactReferences = <T = any>(
  params: Omit<BusinessReferencePageRequest, 'current' | 'pageSize'> = {},
) => getAllBusinessReferenceRows<T>('supplier-contacts', params);

export const getAllVesselReferences = <T = any>(
  params: Omit<BusinessReferencePageRequest, 'current' | 'pageSize'> = {},
) => getAllBusinessReferenceRows<T>('vessels', params);

export const getAllMeasurementUnitReferences = <T = any>(
  params: Omit<BusinessReferencePageRequest, 'current' | 'pageSize'> = {},
) => getAllBusinessReferenceRows<T>('measurement-units', params);

export const getAllWarehouseReferences = <T = any>(
  params: Omit<BusinessReferencePageRequest, 'current' | 'pageSize'> = {},
) => getAllBusinessReferenceRows<T>('warehouses', params);

export const getAllCurrencyReferences = <T = any>(
  params: Omit<BusinessReferencePageRequest, 'current' | 'pageSize'> = {},
) => getAllBusinessReferenceRows<T>('currencies', params);

export const getAllProductNameReferences = <T = any>(
  params: Omit<BusinessReferencePageRequest, 'current' | 'pageSize'> = {},
) => getAllBusinessReferenceRows<T>('product-names', params);

export const getAllCodeArchiveReferences = <T = any>(
  params: Omit<BusinessReferencePageRequest, 'current' | 'pageSize'> = {},
) => getAllBusinessReferenceRows<T>('code-archives', params);
